import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { ArrowRight, Compass, UsersRound, Globe2 } from "lucide-react";

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24">
        {/* Hero Banner */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1542626991-cbc4e32524cc?auto=format&fit=crop&w=1920"
              alt="Building Business Standards - Strategic Institution"
              className="w-full h-full object-cover"
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-blue-900/65 to-indigo-900/70"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                Standards for conscious enterprise—built for{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                  real world business
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed">
                Our tiered certification and recognition ecosystem makes ethical excellence visible—and commercially valuable.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <a href="/signup">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                    Explore Certification
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <a href="/about">
                  <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white hover:text-slate-900">
                    Learn More
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
              
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                  <Compass className="h-8 w-8 text-amber-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                  To foster conscious capitalism through comprehensive assessment and certification of organizational practices that benefit people, planet, and profit.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                  <UsersRound className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                  A world where conscious business practices are the standard, driving sustainable growth and positive impact across all industries and communities.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                  <Globe2 className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Impact</h3>
                <p className="text-gray-600 leading-relaxed">
                  Creating a global ecosystem of certified conscious enterprises that demonstrate measurable positive impact on society and the environment.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Our Story</h2>
              
              <div className="prose prose-lg mx-auto text-gray-600">
                <p className="mb-6">
                  CNESS was founded on the belief that business can be a force for good. In a world where stakeholders increasingly demand transparency, accountability, and ethical practices, we recognized the need for a comprehensive certification system that goes beyond traditional compliance.
                </p>
                
                <p className="mb-6">
                  Our framework is built on eight fundamental pillars that represent the core areas where conscious businesses excel. From ethical leadership to environmental stewardship, from data security to continuous evolution, we assess what matters most for sustainable business success.
                </p>
                
                <p className="mb-8">
                  Today, we're proud to support organizations worldwide in their journey toward conscious business practices, providing the tools, recognition, and community they need to create lasting positive impact.
                </p>
              </div>

              <div className="text-center">
                <a href="/signup">
                  <Button size="lg" className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white">
                    Start Your Certification Journey
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">Our Values</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  {
                    title: "Transparency",
                    description: "We believe in open, honest communication and clear processes that build trust and accountability."
                  },
                  {
                    title: "Excellence", 
                    description: "We maintain the highest standards in our certification processes and continuously improve our frameworks."
                  },
                  {
                    title: "Integrity",
                    description: "We operate with unwavering ethical principles and expect the same from our certified organizations."
                  },
                  {
                    title: "Innovation",
                    description: "We embrace new ideas and technologies that advance conscious business practices and positive impact."
                  }
                ].map((value, index) => (
                  <div key={index} className="p-6 bg-gray-50 rounded-lg">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;